// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

// Services
import { UnitService } from '../services/unit.service';
import { CoreService } from '../../../../shared/services/core.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Models
import { UnitViewModel } from '../models/unit-view.model';
import { AppCleanup } from '../../../../shared/models';

// Store
import { UnitActions } from './unit.action';

// Enums
import { StatusType } from '../../../../shared/enums';

export interface UnitStateModel {
	items: UnitViewModel[];
	loaded: boolean;
}

const defaultState: UnitStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'unit',
	defaults: defaultState,
})
@Injectable()
export class UnitState extends AppCleanup {
	constructor(
		private unitService: UnitService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(UnitActions.GetAll)
	getAll(ctx: StateContext<UnitStateModel>, action: UnitActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitService
			.getUnits()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (units) => {
					ctx.setState({
						items: units,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.DEVICE_UNIT.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UnitActions.SetAll)
	setAll(ctx: StateContext<UnitStateModel>, action: UnitActions.SetAll) {
		ctx.setState({
			items: action.units,
			loaded: true,
		});
	}

	@Action(UnitActions.Update)
	update(ctx: StateContext<UnitStateModel>, action: UnitActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitService
			.updateUnit(action.data.unit)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (unit) => {
					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === unit.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((unit) => unit.id === action.data.unit.id, action.data.unit),
							})
						);
					}

					ctx.dispatch(new UnitActions.UpdateSuccess(unit));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.UNIT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new UnitActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.UNIT.TITLE_CAPITAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UnitActions.Modify)
	modify(ctx: StateContext<UnitStateModel>, action: UnitActions.Modify) {
		const currentState = ctx.getState();
		const existingItem = currentState.items?.find((item) => item.id === action.unit.id);

		if (existingItem) {
			ctx.setState(
				patch({
					items: updateItem((unit) => unit.id === action.unit.id, action.unit),
				})
			);
		}
	}

	@Selector()
	static units(state: UnitStateModel): UnitViewModel[] {
		return state.items;
	}

	@Selector()
	static isLoaded(state: UnitStateModel): boolean {
		return state.loaded;
	}
}
